/**
 * Rising guess bubbles — visual feedback overlay for incoming chat answers.
 *
 * Each `ss_guess_received` WS frame spawns a small pill bubble at random X
 * near the bottom of the LEFT content band (the right-side leaderboard sits
 * at x=1500..1880 and must stay clear). The bubble rises ~300px over ~3s,
 * fading out during the last second, then disposes itself.
 *
 * Notes
 *  - Backend rate-limits emission to ~10 frames/sec platform-wide so the
 *    chat firehose can't drown the page in DOM nodes; this layer additionally
 *    soft-caps concurrent bubbles at MAX_BUBBLES (oldest dropped) for safety.
 *  - Guess text is truncated to MAX_LEN chars with an ellipsis suffix.
 *  - Names / user IDs are intentionally NOT rendered (no name reveal during
 *    play — commit 1fc84bd). Only the guess text appears.
 *  - The overlay is positioned inside the 1920x1080 stage so the existing
 *    transform-scale logic (`__ss_stage_scale`) handles all viewport sizing.
 */

const STAGE_W = 1920;
const STAGE_H = 1080;
/** Max simultaneous bubbles in the DOM. Oldest is dropped if exceeded. */
const MAX_BUBBLES = 30;
/** Truncate guess text to this many characters (with ellipsis). */
const MAX_LEN = 16;
/** Bubble overlay zone within the LEFT content band (clear of the LB at 1500). */
const ZONE_X_MIN = 100;
const ZONE_X_MAX = 1180;
/** Y range bubbles spawn within at the bottom of the answer-window section. */
const ZONE_Y_BASE = STAGE_H - 140;
const ZONE_Y_JITTER = 40;

export interface GuessBubblesHandle {
  el: HTMLDivElement;
  push(guess: string): void;
  setVisible(v: boolean): void;
  clear(): void;
}

function ensureKeyframes(): void {
  if (document.getElementById('ss-guess-bubbles-style')) return;
  const s = document.createElement('style');
  s.id = 'ss-guess-bubbles-style';
  s.textContent = `
    @keyframes ss-bubble-rise {
      0%   { transform: translate(-50%, 0) scale(0.85); opacity: 0; }
      8%   { transform: translate(-50%, -20px) scale(1.0); opacity: 1; }
      66%  { transform: translate(-50%, -220px) scale(1.0); opacity: 1; }
      100% { transform: translate(-50%, -320px) scale(0.95); opacity: 0; }
    }
    .ss-guess-bubble {
      position: absolute;
      bottom: 0;
      transform: translate(-50%, 0);
      background: rgba(10, 14, 39, 0.78);
      color: #fff;
      font-family: 'Inter', 'Oswald', sans-serif;
      font-size: 22px;
      font-weight: 600;
      padding: 8px 16px;
      border-radius: 999px;
      border: 2px solid rgba(255, 196, 61, 0.55);
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
      letter-spacing: 0.4px;
      white-space: nowrap;
      max-width: 360px;
      overflow: hidden;
      text-overflow: ellipsis;
      pointer-events: none;
      animation: ss-bubble-rise 3000ms ease-out forwards;
      will-change: transform, opacity;
    }
  `;
  document.head.appendChild(s);
}

function truncate(text: string): string {
  const trimmed = text.trim();
  if (trimmed.length <= MAX_LEN) return trimmed;
  return trimmed.slice(0, MAX_LEN - 1) + '…';
}

export function createGuessBubbles(stage: HTMLElement): GuessBubblesHandle {
  ensureKeyframes();

  const el = document.createElement('div');
  el.setAttribute('data-overlay', 'ss-guess-bubbles');
  el.setAttribute('data-testid', 'ss-guess-bubbles');
  // Bottom-left zone within the 1180px content area. Above the answer-input
  // visual section but below the prompt card. pointer-events: none lets
  // clicks pass through to OBS / dashboard preview tooling.
  el.style.cssText = `
    position: absolute;
    left: 0;
    bottom: 0;
    width: ${ZONE_X_MAX + 60}px;
    height: 360px;
    pointer-events: none;
    z-index: 5;
    overflow: visible;
  `;
  stage.appendChild(el);

  function push(guess: string): void {
    const text = truncate(guess);
    if (!text) return;

    // Soft cap: drop earliest bubble if at the limit. Avoids unbounded DOM
    // growth even if the backend rate-limit slips.
    while (el.children.length >= MAX_BUBBLES) {
      el.firstElementChild?.remove();
    }

    const bubble = document.createElement('div');
    bubble.className = 'ss-guess-bubble';
    bubble.setAttribute('data-testid', 'ss-guess-bubble');
    bubble.textContent = text;
    const x = ZONE_X_MIN + Math.random() * (ZONE_X_MAX - ZONE_X_MIN);
    const yJitter = Math.random() * ZONE_Y_JITTER;
    bubble.style.left = `${x}px`;
    // Position relative to the overlay div: overlay bottom == stage bottom,
    // so `bottom: ${jitter}px` lands just above the bottom edge.
    bubble.style.bottom = `${yJitter}px`;
    el.appendChild(bubble);

    // The CSS animation runs 3000ms; remove on completion. animationend is
    // more robust than a setTimeout because it survives tab-throttling.
    bubble.addEventListener(
      'animationend',
      () => {
        bubble.remove();
      },
      { once: true },
    );

    // Defensive timeout in case animationend doesn't fire (e.g. element
    // disconnected). Slightly past the animation duration.
    setTimeout(() => {
      if (bubble.isConnected) bubble.remove();
    }, 3500);

    // Also use ZONE_Y_BASE so referenced (lint-friendly) — keeps the contract
    // explicit even if we don't currently sample within the full Y range.
    void ZONE_Y_BASE;
  }

  function setVisible(v: boolean): void {
    el.style.display = v ? 'block' : 'none';
    if (!v) clear();
  }

  function clear(): void {
    while (el.firstChild) el.firstChild.remove();
  }

  return { el, push, setVisible, clear };
}
